import { useFormik } from "formik";
import { Button } from "primereact/button";
import React from "react";

import { useFragment, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type editExternalLicensePoolForm_EditExternalLicensePoolMutation } from "@relay/editExternalLicensePoolForm_EditExternalLicensePoolMutation.graphql";
import { type editExternalLicensePoolForm_ExternalLicensePoolFragment$key } from "@relay/editExternalLicensePoolForm_ExternalLicensePoolFragment.graphql";
import {
	EDIT_EXTERNAL_LICENSE_POOL_MUTATION,
	EXTERNAL_LICENSE_POOL_FRAGMENT,
} from "./edit-external-license-pool-form.graphql";
import {
	type EditExternalLicensePoolFormFormState,
	type EditExternalLicensePoolFormProps,
} from "./edit-external-license-pool-form.interface";
import {
	DefaultIntegerFieldComponent,
	DefaultTextFieldComponent,
} from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import { useCallbackPrompt } from "../../../../hooks/UseCallBackPrompt";
import { Paths } from "../../../../routes/paths";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
	selectArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store.redux";

export const EditExternalLicensePoolForm = ({
	loading,
	entityRef,
}: EditExternalLicensePoolFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const navigate = useNavigate();
	const dispatch = useTypedDispatch();
	const arrayOfEditedForms = useTypedSelector(selectArrayOfEditedForms);
	const [showPrompt, confirmNavigation, cancelNavigation, setShowPrompt] = useCallbackPrompt(
		arrayOfEditedForms.length > 0,
	);

	const [updateExternalLicensePool] =
		useMutation<editExternalLicensePoolForm_EditExternalLicensePoolMutation>(
			EDIT_EXTERNAL_LICENSE_POOL_MUTATION,
		);

	const externalLicensePool =
		useFragment<editExternalLicensePoolForm_ExternalLicensePoolFragment$key>(
			EXTERNAL_LICENSE_POOL_FRAGMENT,
			entityRef,
		);

	const onSubmit = (values: EditExternalLicensePoolFormFormState) => {
		updateExternalLicensePool({
			variables: {
				input: {
					id: externalLicensePool.id!,
					data: {
						name: values.name ?? "",
						usageInformation: values.usageInformation,
						validNumDaysAfterIssuing: values.validNumDaysAfterIssuing,
						link: values.link ?? "",
					},
				},
			},
		});
	};

	const formId = "EditExternalLicensePoolForm";
	const formik = useFormik<EditExternalLicensePoolFormFormState>({
		initialValues: {
			name: externalLicensePool.data.name ?? t("common:externalLicenses.newPool"),
			usageInformation:
				externalLicensePool.data.usageInformation ??
				t("common:externalLicenses.usageInfoDefault"),
			validNumDaysAfterIssuing: externalLicensePool.data.validNumDaysAfterIssuing ?? "1",
			link: externalLicensePool.data.link ?? "",
		},
		validationSchema: Yup.object().shape({
			name: Yup.string().required(t("validation:externalLicenseNameRequired")),
			validNumDaysAfterIssuing: Yup.number().required(
				t("validation:externalLicenseValidDaysRequired"),
			),
			link: Yup.string().url(t("validation:externalLicenseInvalidUrl")),
		}),
		onSubmit: (values, { setSubmitting }) => {
			onSubmit({
				name: values.name,
				usageInformation: values.usageInformation,
				validNumDaysAfterIssuing: values.validNumDaysAfterIssuing,
				link: values.link,
			});
			setSubmitting(false);
			dispatch(resetArrayOfEditedForms());
			toast.success(t("common:messages.saveSuccess"));
		},
	});

	return (
		<>
			{showPrompt && (
				<WarningUnsavedChangesDialog
					confirmNavigation={confirmNavigation}
					setShowDialog={setShowPrompt}
					cancelNavigation={cancelNavigation}
				/>
			)}
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<EditExternalLicensePoolFormFormState, string>
					name={"name"}
					label={t("common:table.name")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<EditExternalLicensePoolFormFormState, string>
					name={"usageInformation"}
					label={t("common:externalLicenses.usageInformation")}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.Default}
						/>
					)}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>

				<ValidatedField<EditExternalLicensePoolFormFormState, number>
					name={"validNumDaysAfterIssuing"}
					label={t("common:externalLicenses.validDaysLabel")}
					component={DefaultIntegerFieldComponent}
					formikConfig={formik}
				/>
				<ValidatedField<EditExternalLicensePoolFormFormState, string>
					name={"link"}
					label={t("common:externalLicenses.linkLabel")}
					component={DefaultTextFieldComponent}
					formikConfig={formik}
				/>

				<Button
					disabled={loading}
					type="submit"
					label={t("common:actions.save")}
					className="mt-2"
				/>
				<Button
					disabled={loading}
					type="button"
					label={t("common:actions.back")}
					onClick={() => {
						navigate(Paths.externalLicensePools.path);
					}}
					className="mt-2"
				/>
			</form>
		</>
	);
};
